import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils/tailwind';

import type {
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react';

const listItemVariants = cva(
  'flex items-center gap-2 rounded-lg transition cursor-pointer',
  {
    variants: {
      selected: {
        true: 'bg-blue-100 border-blue-500',
        false: 'bg-white border-gray-300 hover:bg-gray-100',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: '',
      },
    },
    defaultVariants: {
      selected: false,
      disabled: false,
    },
  },
);

interface ItemProps
  extends
    Omit<HTMLAttributes<HTMLLIElement>, 'prefix'>,
    VariantProps<typeof listItemVariants> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  children: ReactNode;
  className?: string;
  onClick?: (
    event: MouseEvent<HTMLLIElement> | KeyboardEvent<HTMLLIElement>,
  ) => void;
  ref?: Ref<HTMLLIElement>;
}

const Item = ({
  prefix,
  suffix,
  selected,
  disabled,
  children,
  className,
  ref,
  onClick,
  ...props
}: ItemProps) => {
  const isClickable = typeof onClick === 'function' && !disabled;

  return (
    <li
      className={cn(listItemVariants({ selected, disabled }), className)}
      ref={ref}
      tabIndex={isClickable ? 0 : undefined}
      aria-disabled={disabled ? 'true' : undefined}
      role={isClickable ? 'button' : undefined}
      {...props}
      {...(isClickable && {
        onClick: event => {
          event.preventDefault();
          onClick?.(event);
        },
        onKeyDown: event => {
          const target = event.target as HTMLElement;
          const isTypingTarget = ['INPUT'].includes(target.tagName);
          if (isTypingTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.(event as unknown as MouseEvent<HTMLLIElement>);
          }
        },
      })}
    >
      {prefix && <span className="shrink-0">{prefix}</span>}
      <span className="flex-1 min-w-0">{children}</span>
      {suffix && <span className="shrink-0">{suffix}</span>}
    </li>
  );
};

export { Item, type ItemProps };
